/**
 * The plan gate — visible-but-locked, never hidden.
 *
 * Always three things: what the feature is, why it costs money, and one honest
 * non-upgrade path where one exists. Hiding a gated feature makes the product
 * look smaller than it is and leaves the user unable to tell whether the thing
 * they need exists at all.
 *
 * ## The recall carve-out is a throw, not a convention
 *
 * Product invariant 3, and `acceptance-tests.md` states it as an absolute:
 * **"No upgrade prompt appears anywhere in the recall flow, at any tier"** —
 * *including* where a missing batch record is created inline, which is the
 * exact spot a gate would otherwise be natural, because batch management IS a
 * paid feature and the recall flow creates batch records.
 *
 * That is the whole reason this is enforced rather than documented. The
 * violating code is the *reasonable* code: someone builds inline batch
 * creation, reuses the batch component, and the batch component carries its
 * gate. Nobody decides to put an upgrade prompt in a recall flow; it arrives by
 * composition.
 *
 * So {@link RecallFlow} marks the boundary and a PlanGate inside it throws in
 * development. The same shape as Surface's nesting refusal, for the same
 * reason: the rule is about what a user sees when composition puts two correct
 * components together.
 */
import { createContext, createElement, useContext } from "react";
import { Surface } from "../surface/Surface.js";
const InRecallFlow = createContext(false);
/**
 * Marks a subtree as recall flow. Wrap the whole flow, including any inline
 * record creation it does — that is the part the invariant calls out by name.
 */
export function RecallFlow({ children }) {
    return createElement(InRecallFlow.Provider, { value: true }, children);
}
export function PlanGate({ plan, feature, why, instead, children, }) {
    const inRecallFlow = useContext(InRecallFlow);
    if (process.env.NODE_ENV !== "production" && inRecallFlow) {
        throw new Error(`PlanGate ("${feature}") rendered inside a RecallFlow. No upgrade prompt may ` +
            `appear anywhere in the recall flow, at any tier or billing status — ` +
            `including where a missing batch record is created inline, which is ` +
            `usually how this happens: a gated component reused inside the flow ` +
            `brings its gate with it. The safety path is never gated. Render the ` +
            `feature ungated here, or render nothing.`);
    }
    return createElement(Surface, { tone: "neutral", emphasis: "flat", size: "card", className: "pp-gate" }, createElement("p", { key: "f", className: "pp-gate-feature" }, feature), createElement("p", { key: "w", className: "pp-gate-why" }, why), instead === undefined
        ? null
        : createElement("p", { key: "i", className: "pp-gate-instead" }, instead), createElement("span", { key: "p", className: "pp-gate-plan" }, plan), children);
}
//# sourceMappingURL=PlanGate.js.map